import { CmsProduct } from '../../types/cmsProduct';
import { formatAttributeItemValue } from '../../utils/helpers/attrHelpers';
import { QuickAnswerAttribute } from './types';

/** Find the product attribute matching a QuickAnswer column (by uid). */
const findProductAttribute = (
  product: CmsProduct,
  attr: QuickAnswerAttribute,
) => {
  if (!attr.attribute?.uid || !product.attributes?.length) return undefined;
  return product.attributes.find((a) => a.name?.uid === attr.attribute!.uid);
};

/**
 * Resolve a non-reward attribute cell value for a given product.
 * Returns undefined when there is no match / no value (caller renders the NA placeholder).
 */
export const resolveAttributeCellValue = (
  product: CmsProduct,
  attr: QuickAnswerAttribute,
): string | undefined => {
  const match = findProductAttribute(product, attr);
  if (!match) return undefined;

  // formatNumber: true — numeric values need a thousands separator ("50,000"),
  // matching ComparisonTable and the Figma design.
  const formatted = formatAttributeItemValue(match, false, true);
  return formatted && formatted.trim() && formatted.trim() !== '-'
    ? formatted
    : undefined;
};

/**
 * Resolve the attribute's "Additional information" tooltip content (CMS-1790).
 * Mirrors ComparisonTable.getAdditionalInformation: the field only exists on
 * Number / NumberRange catalogue attributes; every other type has no tooltip.
 */
export const resolveAttributeAdditionalInfo = (
  product: CmsProduct,
  attr: QuickAnswerAttribute,
): string | undefined => {
  const match = findProductAttribute(product, attr);
  if (!match) return undefined;

  switch (match.__typename) {
    case 'ComponentAttributesAttrNumber':
    case 'ComponentAttributesAttrNumberRange':
      return match.additional_information?.trim() || undefined;
    default:
      return undefined;
  }
};
